import React, { useState, useRef } from 'react';
import { Accordion, AccordionItem, addToast, Tabs, Tab, Button, Input, Card, CardBody, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Select, SelectItem } from '@heroui/react';
import { updateExcercise, deleteExercisePhase, uploadMotionRecordingData, uploadExerciseVideoPreview, uploadAnimationFile, uploadExcerciseRefVideo, uploadExcerciseRefAnim, fetchExcercises, mirrorExerciseReference } from '../../services/api';
import ExerciseHeader from '../ExerciseHeader/ExerciseHeader';
import KeteranganDivider from '../KeteranganDivider/KeteranganDevider';
import ReferenceMotion from '../ReferenceMotion/ReferenceMotion';
import CloudVizPlayer from '../CloudViz/CloudVizPlayer';
import { DocumentTextIcon, ClockIcon, FolderIcon, VideoCameraIcon, CheckIcon, XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import './ExerciseDetail.css';

function uncombinedMovementRules(movementRules){
  const bodyPartsInAction= Object.getOwnPropertyNames(movementRules);
  
  let result=[];
  for (let index = 0; index < bodyPartsInAction.length; index++) {
      const element = bodyPartsInAction[index];
      let uncombinedParts=findUncombinedBodyParts(element);
      result=[...result,...uncombinedParts]
  }
  return result; 
}

//TODO: duplicate with referenceMotion
function findUncombinedBodyParts(bodyPart){
  let result=[];
  switch (bodyPart) {
      case "shoulders":
          result=["lShoulder","rShoulder"]
          break;
      case "elbows":
          result=["lElbow","rElbow"]
          break;
      case "wrists":
          result=["lWrist","rWrist"]
          break;
      case "thighs":
          result=["lThigh","rThigh"]
          break;
      case "knees":
          result=["lKnee","rKnee"]
          break;
      case "ankles":
          result=["lAnkle","rAnkle"]
          break;
      default:
          result=[bodyPart]
          break;
  }
  return result;
}

// Function untuk mendapatkan bone yang benar-benar aktif (tidak termasuk yang sudah dihapus)
function getActiveBones(referenceMotionData = []) {
  const boneSet = new Set();

  referenceMotionData.forEach((phase) => {
    const rules = phase?.movementRules || {};
    const limits = phase?.maxAngleLimits || {};
    const priorities = phase?.feedbackPriority || [];

    Object.keys(rules).forEach((bone) => boneSet.add(bone));
    Object.keys(limits).forEach((bone) => boneSet.add(bone));
    priorities.forEach((bone) => boneSet.add(bone));
  });

  const groupMap = {
    shoulders: ['lShoulder', 'rShoulder'],
    elbows: ['lElbow', 'rElbow'],
    wrists: ['lWrist', 'rWrist'],
    thighs: ['lThigh', 'rThigh'],
    knees: ['lKnee', 'rKnee'],
    ankles: ['lAnkle', 'rAnkle']
  };

  // Jika bone individual ada, hapus group agar tidak dobel
  Object.entries(groupMap).forEach(([group, individuals]) => {
    const hasIndividual = individuals.some((bone) => boneSet.has(bone));
    if (hasIndividual) {
      boneSet.delete(group);
    }
  });

  // Langsung ambil bone yang ada di data
  const activeBones = Array.from(boneSet);

  // Jika ada bone group, expand ke bone individual
  const result = new Set();
  activeBones.forEach(bone => {
    if (bone === 'shoulders') {
      result.add('lShoulder');
      result.add('rShoulder');
    } else if (bone === 'elbows') {
      result.add('lElbow');
      result.add('rElbow');
    } else if (bone === 'wrists') {
      result.add('lWrist');
      result.add('rWrist');
    } else if (bone === 'thighs') {
      result.add('lThigh');
      result.add('rThigh');
    } else if (bone === 'knees') {
      result.add('lKnee');
      result.add('rKnee');
    } else if (bone === 'ankles') {
      result.add('lAnkle');
      result.add('rAnkle');
    } else {
      result.add(bone);
    }
  });

  // Simply return the result - bone group logic is handled in handleRemoveBone
  // When a bone is removed from a group, the group is converted to individual bones
  return Array.from(result);
}

// Function untuk mendapatkan bone berpasangan
function getPairedBone(bone) {
  switch (bone) {
    case "lShoulder":
      return "rShoulder";
    case "rShoulder":
      return "lShoulder";
    case "lElbow":
      return "rElbow";
    case "rElbow":
      return "lElbow";
    case "lWrist":
      return "rWrist";
    case "rWrist":
      return "lWrist";
    case "lThigh":
      return "rThigh";
    case "rThigh":
      return "lThigh";
    case "lKnee":
      return "rKnee";
    case "rKnee":
      return "lKnee";
    case "lAnkle":
      return "rAnkle";
    case "rAnkle":
      return "lAnkle";
    default:
      return null;
  }
}

// Function untuk mendapatkan group bone
function getBoneGroup(bone) {
  switch (bone) {
    case "lShoulder":
    case "rShoulder":
      return "shoulders";
    case "lElbow":
    case "rElbow":
      return "elbows";
    case "lWrist":
    case "rWrist":
      return "wrists";
    case "lThigh":
    case "rThigh":
      return "thighs";
    case "lKnee":
    case "rKnee":
      return "knees";
    case "lAnkle":
    case "rAnkle":
      return "ankles";
    default:
      return bone;
  }
}

const ExcerciseDetail = ({ item, onItemUpdated }) => {
  const [selectedTab, setSelectedTab] = useState("basic");
  const [isMotionRecordingUploading, setIsMotionRecordingUploading] = useState(false);
  const motionRecordingInputRef = useRef(null);
  
  // State untuk animation file upload
  const [isAnimationUploading, setIsAnimationUploading] = useState(false);
  const animationInputRef = useRef(null);

  const [selectedPreviewPhaseName, setSelectedPreviewPhaseName] = useState('');

  const [isMirrorModalOpen, setIsMirrorModalOpen] = useState(false);
  const [mirrorTargetId, setMirrorTargetId] = useState('');
  const [mirrorOptions, setMirrorOptions] = useState([]);
  const [isMirrorLoading, setIsMirrorLoading] = useState(false);
  const [isMirrorSubmitting, setIsMirrorSubmitting] = useState(false);
  
  const bodyParts=["neck",
    "lShoulder",
    "rShoulder",
    "lElbow",
    "rElbow",
    "lWrist",
    "rWrist",
    "waist",
    "lThigh",
    "rThigh",
    "lKnee",
    "rKnee",
    "lAnkle",
    "rAnkle",
    "spine"
    ];
  
  // Local state untuk item agar bisa diupdate
  const [localItem, setLocalItem] = useState(item);
  
  // Sync localItem with prop item when it changes
  React.useEffect(() => {
    setLocalItem(item);
  }, [item]);
  
  // State untuk batch updates - menyimpan perubahan di memory
  const [pendingChanges, setPendingChanges] = useState({
    begin: null,
    middle: null,
    end: null
  });

  // State untuk tracking phase yang akan dihapus
  const [phasesToDelete, setPhasesToDelete] = useState([]);
  
  // State untuk tracking save status
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Update itemData when localItem changes - only show active bones
  React.useEffect(() => {
    // Get active bones from movementRules
    const activeBones = getActiveBones(localItem.referenceMotionData || []);
   // Sort reference motion data to ensure correct order: begin, middle, end
    const sortedReferenceMotionData = localItem.referenceMotionData?.sort((a, b) => {
      const order = { begin: 0, middle: 1, end: 2 };
      return order[a.name] - order[b.name];
    }) || [];
    
    // Only create items for active bones
    const updatedItems = activeBones.map(part => ({
      bone: part,
      beginData: {
        name: 'begin',
        ...sortedReferenceMotionData.find(phase => phase.name === 'begin') || {}
      },
      middleData: {
        name: 'middle',
        ...sortedReferenceMotionData.find(phase => phase.name === 'middle') || {}
      },
      endData: {
        name: 'end',
        ...sortedReferenceMotionData.find(phase => phase.name === 'end') || {}
      }
    }));
    
    setItemData(updatedItems);
    
    // Update defaultExpandedKeys to match active bones
    setDefaultExpandedKeys(activeBones);
  }, [localItem]);
  
  // State untuk tracking phase yang sedang diupload
  const [editingPhase, setEditingPhase] = useState(null);
  
  // State untuk video upload
  const [isVideoUploading, setIsVideoUploading] = useState(false);
  const videoInputRef = useRef(null);
  
  const [itemData, setItemData]=useState([]);
  const [defaultExpandedKeys, setDefaultExpandedKeys] = useState([]);
  
  // State untuk add bone modal
  const [isAddBoneModalOpen, setIsAddBoneModalOpen] = useState(false);

  React.useEffect(() => {
    if (!isMirrorModalOpen || mirrorOptions.length > 0) {
      return;
    }

    const loadMirrorOptions = async () => {
      setIsMirrorLoading(true);
      try {
        const response = await fetchExcercises();
        setMirrorOptions(response.data || []);
      } catch (error) {
        addToast({
          title: "Load Error",
          description: "Failed to load exercise list for mirroring",
          color: "danger"
        });
      } finally {
        setIsMirrorLoading(false);
      }
    };

    loadMirrorOptions();
  }, [isMirrorModalOpen, mirrorOptions.length]);

  React.useEffect(() => {
    const sortedPhases = [...(localItem.referenceMotionData || [])].sort((a, b) => {
      const order = { begin: 0, middle: 1, end: 2 };
      return order[a.name] - order[b.name];
    });

    if (sortedPhases.length === 0) {
      setSelectedPreviewPhaseName('');
      return;
    }

    if (sortedPhases.some((phase) => phase.name === selectedPreviewPhaseName)) {
      return;
    }

    const firstPhaseWithPath = sortedPhases.find((phase) => phase.path);
    setSelectedPreviewPhaseName((firstPhaseWithPath || sortedPhases[0]).name);
  }, [localItem.referenceMotionData, selectedPreviewPhaseName]);
  
  // Show only active bones from reference motion data
  
  // Function untuk menyimpan perubahan di memory (tidak langsung ke server)
  const handleBoneChange = (part, newData, index) => {
    try {
      // Determine section based on the index function result
      const sectionIndex = index();
      let section;
      if (sectionIndex === 0) section = 'begin';
      else if (sectionIndex === 1) section = 'middle';
      else if (sectionIndex === 2) section = 'end';
      else throw new Error('Invalid section index');
      
      // Prepare motion data - only send 3 fields that server needs
      // Server will ignore other fields, so no need to send them
      const motionData = {
        name: section,
        movementRules: newData.movementRules || {},
        maxAngleLimits: newData.maxAngleLimits || {},
        feedbackPriority: newData.feedbackPriority || []
      };
      
      // Save to pending changes instead of sending to server
      setPendingChanges(prev => ({
        ...prev,
        [section]: motionData
      }));
      
      // Mark that we have unsaved changes
      setHasUnsavedChanges(true);
      
      // Update local UI state for immediate feedback
        setItemData(prev => prev.map(item => {
        const shouldUpdateBegin = section === 'begin';
        const shouldUpdateMiddle = section === 'middle';
        const shouldUpdateEnd = section === 'end';
          
          return {
            ...item,
            beginData: shouldUpdateBegin ? {
              ...item.beginData,
              movementRules: newData.movementRules,
              maxAngleLimits: newData.maxAngleLimits,
              feedbackPriority: newData.feedbackPriority
            } : item.beginData,
            
            middleData: shouldUpdateMiddle ? {
              ...item.middleData,
              movementRules: newData.movementRules,
              maxAngleLimits: newData.maxAngleLimits,
              feedbackPriority: newData.feedbackPriority
            } : item.middleData,
            
            endData: shouldUpdateEnd ? {
              ...item.endData,
              movementRules: newData.movementRules,
              maxAngleLimits: newData.maxAngleLimits,
              feedbackPriority: newData.feedbackPriority
            } : item.endData
          };
        }));
      
      // Show notification that changes are saved locally
      /*addToast({
        title: "Changes Saved Locally",
        description: `${section} phase changes saved to memory. Click Save to update server.`,
        color: "primary"
      });*/
      
      } catch (error) {
        addToast({
          title: "Update Error",
          description: error,
          color:"danger"
        });
      }
  };

  // Function untuk save semua perubahan ke server
  const handleSaveAllChanges = async () => {
    if (!hasUnsavedChanges) {
      addToast({
        title: "No Changes",
        description: "No changes to save",
        color: "warning"
      });
      return;
    }

    setIsSaving(true);
    
    try {
      // Process phase deletions first
      const deletePromises = phasesToDelete.map(section =>
        deleteExercisePhase(localItem.id, section)
          .catch(error => {
            console.error('Error deleting section', section, ':', error);
            throw error;
          })
      );
      if (deletePromises.length > 0) {
        await Promise.all(deletePromises);
      }

      // Update each phase that has pending changes and collect responses
      const sectionsToUpdate = Object.entries(pendingChanges)
        .filter(([section, data]) => data !== null);
      
      let updatedReferenceMotionData;

      if (sectionsToUpdate.length === 0 && phasesToDelete.length === 0) {
        setHasUnsavedChanges(false);
        addToast({
          title: "No Changes",
          description: "No valid changes to save",
          color: "warning"
        });
        return;
      }

      if (sectionsToUpdate.length > 0) {
        const updatePromises = sectionsToUpdate.map(([section, data]) => 
          updateExcercise(localItem.id, section, data)
            .then(response => ({ section, response: response.data }))
            .catch(error => {
              console.error('Error updating section', section, ':', error);
              throw error;
            })
        );
        const updateResults = await Promise.all(updatePromises);
        const lastUpdateResult = updateResults[updateResults.length - 1];
        updatedReferenceMotionData = lastUpdateResult.response;
      } else {
        // Only deletions happened - fetch fresh data from server
        const { fetchSingleExcercise } = await import('../../services/api');
        const freshResponse = await fetchSingleExcercise(localItem.id);
        updatedReferenceMotionData = freshResponse.data.referenceMotionData;
      }
      
      console.log('Updated referenceMotionData:', updatedReferenceMotionData);
      
      const updatedLocalItem = {
        ...localItem,
        referenceMotionData: updatedReferenceMotionData
      };
      
      console.log('Updated localItem:', updatedLocalItem);
      
      // Update local state
      setLocalItem(updatedLocalItem);
      
      // Call parent callback
      if (onItemUpdated) {
        onItemUpdated(updatedLocalItem);
      }
      
      // Clear pending changes and phase deletions
      setPendingChanges({
        begin: null,
        middle: null,
        end: null
      });
      setPhasesToDelete([]);
      
      // Reset unsaved changes flag
      setHasUnsavedChanges(false);
      
      addToast({
        title: "Success",
        description: "All reference motion changes saved successfully!",
        color: "success"
      });
      
    } catch (error) {
      console.error('Save error:', error);
      addToast({
        title: "Save Error",
        description: "Failed to save changes. Please try again.",
        color: "danger"
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Function untuk menghapus bone dari reference motion data (memory only)
  const handleRemoveBone = (boneToRemove) => {
    try {

      // Remove bone from all phases (begin, middle, end) and add to pending changes
      const updatedReferenceMotionData = localItem.referenceMotionData?.map(phase => {
        const updatedMovementRules = { ...phase.movementRules };
        const updatedMaxAngleLimits = { ...phase.maxAngleLimits };
        const updatedFeedbackPriority = [...(phase.feedbackPriority || [])];
        
        // Remove individual bone from movementRules
        delete updatedMovementRules[boneToRemove];
        
        // Remove individual bone from maxAngleLimits
        delete updatedMaxAngleLimits[boneToRemove];
        
        // Remove individual bone from feedbackPriority
        const filteredFeedbackPriority = updatedFeedbackPriority.filter(item => item !== boneToRemove);
        
        // Handle bone group logic: if removing from a group, convert group to individual bones
        const boneGroup = getBoneGroup(boneToRemove);
        if (boneGroup !== boneToRemove && updatedMovementRules[boneGroup]) {
          // Remove the group
          delete updatedMovementRules[boneGroup];
          delete updatedMaxAngleLimits[boneGroup];
          const finalFeedbackPriority = filteredFeedbackPriority.filter(item => item !== boneGroup);
          
          // Add the remaining individual bone from the group
          const pairedBone = getPairedBone(boneToRemove);
          if (pairedBone) {
            // Add the paired bone as individual
            updatedMovementRules[pairedBone] = 'move'; // Default value
            updatedMaxAngleLimits[pairedBone] = updatedMaxAngleLimits[pairedBone] || {};
            finalFeedbackPriority.push(pairedBone);
          }
          
          return {
            ...phase,
            movementRules: updatedMovementRules,
            maxAngleLimits: updatedMaxAngleLimits,
            feedbackPriority: finalFeedbackPriority
          };
        }
        
        return {
          ...phase,
          movementRules: updatedMovementRules,
          maxAngleLimits: updatedMaxAngleLimits,
          feedbackPriority: filteredFeedbackPriority
        };
      }) || [];

      // Add all phases to pending changes since bone was removed
      const newPendingChanges = {
        begin: updatedReferenceMotionData[0] || null,
        middle: updatedReferenceMotionData[1] || null,
        end: updatedReferenceMotionData[2] || null
      };
      
      // Update pending changes
      setPendingChanges(newPendingChanges);
      
      // Mark that we have unsaved changes
      setHasUnsavedChanges(true);
      
      // Update local item for UI
      const updatedLocalItem = {
        ...localItem,
        referenceMotionData: updatedReferenceMotionData
      };
      
      // Update local state - useEffect will handle itemData update
      setLocalItem(updatedLocalItem);
      
      addToast({
        title: "Bone Removed Locally",
        description: `${boneToRemove} has been removed from all phases. Click "Save Changes" to update server.`,
        color: "primary"
      });
      
    } catch (error) {
      console.error('Remove bone error:', error);
      addToast({
        title: "Remove Error",
        description: "Failed to remove bone. Please try again.",
        color: "danger"
      });
    }
  };

  // Function untuk menghapus phase dari reference motion data (memory only)
  const handleRemovePhase = (phaseNameToRemove) => {
    try {
      // Prevent removing begin phase
      if (phaseNameToRemove === 'begin') {
        addToast({
          title: "Cannot Delete",
          description: `${phaseNameToRemove.charAt(0).toUpperCase() + phaseNameToRemove.slice(1)} phase is required and cannot be deleted`,
          color: "warning"
        });
        return;
      }

      // Remove phase from referenceMotionData
      const updatedReferenceMotionData = localItem.referenceMotionData?.filter(phase => phase.name !== phaseNameToRemove) || [];

      // Remove any pending changes for this phase
      const newPendingChanges = { ...pendingChanges };
      newPendingChanges[phaseNameToRemove] = null;
      setPendingChanges(newPendingChanges);

      // Track phase deletion separately
      setPhasesToDelete(prev => [...new Set([...prev, phaseNameToRemove])]);

      // Mark that we have unsaved changes
      setHasUnsavedChanges(true);

      // Update local item for UI
      const updatedLocalItem = {
        ...localItem,
        referenceMotionData: updatedReferenceMotionData
      };

      // Update local state - useEffect will handle itemData update
      setLocalItem(updatedLocalItem);

      addToast({
        title: "Phase Removed Locally",
        description: `${phaseNameToRemove.charAt(0).toUpperCase() + phaseNameToRemove.slice(1)} phase has been removed. Click "Save Changes" to update server.`,
        color: "primary"
      });
    } catch (error) {
      console.error('Remove phase error:', error);
      addToast({
        title: "Remove Error",
        description: "Failed to remove phase. Please try again.",
        color: "danger"
      });
    }
  };

  // Function untuk menambah bone ke reference motion data (memory only)
  const handleAddBone = () => {
    setIsAddBoneModalOpen(true);
  };

  const handleOpenMirrorModal = () => {
    setIsMirrorModalOpen(true);
  };

  const handleCloseMirrorModal = () => {
    setIsMirrorModalOpen(false);
    setMirrorTargetId('');
  };

  const handleConfirmMirror = async () => {
    if (!mirrorTargetId) {
      addToast({
        title: "Target Required",
        description: "Please select a target exercise",
        color: "warning"
      });
      return;
    }

    if (String(localItem.id) === String(mirrorTargetId)) {
      addToast({
        title: "Invalid Target",
        description: "Target exercise must be different",
        color: "warning"
      });
      return;
    }

    setIsMirrorSubmitting(true);
    try {
      await mirrorExerciseReference(localItem.id, mirrorTargetId);
      addToast({
        title: "Mirror Success",
        description: `Reference motion mirrored to exercise ${mirrorTargetId}`,
        color: "success"
      });
      handleCloseMirrorModal();
    } catch (error) {
      addToast({
        title: "Mirror Error",
        description: "Failed to mirror reference motion",
        color: "danger"
      });
    } finally {
      setIsMirrorSubmitting(false);
    }
  };

  // Function untuk menambah bone yang dipilih
  const handleAddSelectedBone = (boneToAdd) => {
    try {

      
      // Create default phases if referenceMotionData is null/undefined/empty
      const currentReferenceMotionData = (localItem.referenceMotionData && localItem.referenceMotionData.length > 0) ? localItem.referenceMotionData : [
        {
          name: 'begin',
          duration: 1,
          path: null,
          format: 'TSFrameLog',
          videoPath: null,
          movementRules: {},
          maxAngleLimits: {},
          feedbackPriority: []
        },
        {
          name: 'middle',
          duration: 1,
          path: null,
          format: 'TSFrameLog',
          videoPath: null,
          movementRules: {},
          maxAngleLimits: {},
          feedbackPriority: []
        },
        {
          name: 'end',
          duration: 1,
          path: null,
          format: 'TSFrameLog',
          videoPath: null,
          movementRules: {},
          maxAngleLimits: {},
          feedbackPriority: []
        }
      ];
      
      
      
      // Add bone to all phases (begin, middle, end) and add to pending changes
      const updatedReferenceMotionData = currentReferenceMotionData.map(phase => {
        const updatedMovementRules = { ...phase.movementRules };
        const updatedMaxAngleLimits = { ...phase.maxAngleLimits };
        const updatedFeedbackPriority = [...(phase.feedbackPriority || [])];
        
        // Add individual bone to movementRules
        updatedMovementRules[boneToAdd] = 'move'; // Default value
        
        // Add individual bone to maxAngleLimits with default values [0, 0]
        updatedMaxAngleLimits[boneToAdd] = [0, 0];
        
        // Add individual bone to feedbackPriority if not already present
        if (!updatedFeedbackPriority.includes(boneToAdd)) {
          updatedFeedbackPriority.push(boneToAdd);
        }
        
        return {
          ...phase,
          movementRules: updatedMovementRules,
          maxAngleLimits: updatedMaxAngleLimits,
          feedbackPriority: updatedFeedbackPriority
        };
      });

      // Add all phases to pending changes since bone was added
      const newPendingChanges = {
        begin: updatedReferenceMotionData[0],
        middle: updatedReferenceMotionData[1],
        end: updatedReferenceMotionData[2]
      };
      
      
      
      // Update pending changes
      setPendingChanges(newPendingChanges);
      
      // Mark that we have unsaved changes
      setHasUnsavedChanges(true);
      
      // Update local item for UI
      const updatedLocalItem = {
        ...localItem,
        referenceMotionData: updatedReferenceMotionData
      };
      
      // Update local state - useEffect will handle itemData update
      setLocalItem(updatedLocalItem);
      
      // Close modal
      setIsAddBoneModalOpen(false);
      
     /*  addToast({
        title: "Bone Added Locally",
        description: `${boneToAdd} has been added to all phases. Click "Save Changes" to update server.`,
        color: "primary"
      }); */
      
    } catch (error) {
      addToast({
        title: "Add Error",
        description: "Failed to add bone. Please try again.",
        color: "danger"
      });
    }
  };

  // Function untuk discard semua perubahan
  const handleDiscardChanges = () => {
    setPendingChanges({
      begin: null,
      middle: null,
      end: null
    });
    setPhasesToDelete([]);
    setHasUnsavedChanges(false);
    
    // Reset localItem to original state from props
    setLocalItem(item);
    
    // Reset itemData to original state - only active bones from original item
    const activeBones = getActiveBones(item.referenceMotionData || []);
     // Sort reference motion data to ensure correct order: begin, middle, end
    const sortedReferenceMotionData = item.referenceMotionData?.sort((a, b) => {
      const order = { begin: 0, middle: 1, end: 2 };
      return order[a.name] - order[b.name];
    }) || [];
    
    const updatedItems = activeBones.map(part => ({
      bone: part,
      beginData: {
        name: 'begin',
        ...sortedReferenceMotionData.find(phase => phase.name === 'begin') || {}
      },
      middleData: {
        name: 'middle',
        ...sortedReferenceMotionData.find(phase => phase.name === 'middle') || {}
      },
      endData: {
        name: 'end',
        ...sortedReferenceMotionData.find(phase => phase.name === 'end') || {}
      }
    }));
    setItemData(updatedItems);
    
    addToast({
      title: "Changes Discarded",
      description: "All unsaved changes have been discarded",
      color: "warning"
    });
  };

  const handleMotionRecordingUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.txt')) {
      addToast({
        title: "Invalid File",
        description: "Please select a .txt file",
        color: "warning"
      });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      addToast({
        title: "File Too Large",
        description: "File size should be less than 10MB",
        color: "warning"
      });
      return;
    }

    // Upload to server
    setIsMotionRecordingUploading(true);
    try {
      const response = await uploadMotionRecordingData(localItem.id, file);
      
      setIsMotionRecordingUploading(false);
      
      // Show success notification
      addToast({
        title: "Success",
        description: "Motion recording data uploaded successfully!",
        color: "success"
      });
      
      // Here you would typically update the item.motionRecordingData
    } catch (error) {
      console.error('Upload error:', error);
      setIsMotionRecordingUploading(false);
      
      // Show error notification
      addToast({
        title: "Upload Error",
        description: "Failed to upload motion recording data. Please try again.",
        color: "danger"
      });
    }
  };

  const handleMotionRecordingButtonClick = () => {
    motionRecordingInputRef.current?.click();
  };

  // Handle video upload for phase
  const handleVideoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('video/')) {
      addToast({
        title: "Invalid File",
        description: "Please select a video file",
        color: "warning"
      });
      return;
    }

    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      addToast({
        title: "File Too Large",
        description: "File size should be less than 50MB",
        color: "warning"
      });
      return;
    }

    // Upload to server
    setIsVideoUploading(true);
    try {
     // console.log('handleVideoUpload - editingPhase:', editingPhase);
     // console.log('handleVideoUpload - localItem.id:', localItem.id);
      
      // Validate editingPhase before calling API
      const validSections = ['begin', 'middle', 'end'];
      if (!editingPhase || !validSections.includes(editingPhase)) {
        throw new Error(`Invalid editingPhase: ${editingPhase}. Must be one of: ${validSections.join(', ')}`);
      }
      
      // Use the new uploadExcerciseRefVideo method with section parameter
      const response = await uploadExcerciseRefVideo(localItem.id, editingPhase, file);
      
      
      // The API response already contains the updated phase data
      const updatedPhase = response.data;
      
      // Update local state
      const updatedReferenceMotionData = [...(localItem.referenceMotionData || [])];
      const phaseIndex = updatedReferenceMotionData.findIndex(p => p.name === editingPhase);
      
      if (phaseIndex !== -1) {
        updatedReferenceMotionData[phaseIndex] = updatedPhase;
      } else {
        updatedReferenceMotionData.push(updatedPhase);
      }
      
      // Update itemData state
      setItemData(prev => prev.map(item => ({
        ...item,
        beginData: updatedPhase.name === 'begin' ? updatedPhase : item.beginData,
        middleData: updatedPhase.name === 'middle' ? updatedPhase : item.middleData,
        endData: updatedPhase.name === 'end' ? updatedPhase : item.endData
      })));

      
      // Update the local item state to reflect the new video
      const updatedLocalItem = {
        ...localItem,
        referenceMotionData: updatedReferenceMotionData
      };
      
      // Update local state
      setLocalItem(updatedLocalItem);
      
      // Call the parent callback to update the exercise list
      if (onItemUpdated) {
        onItemUpdated(updatedLocalItem);
      }
      
      // Reset editing phase setelah upload berhasil
      setEditingPhase(null);
      
      addToast({
        title: "Success",
        description: `${editingPhase} phase reference video uploaded successfully`,
        color: "success"
      });
      
      setIsVideoUploading(false);
    } catch (error) {
      console.error('Upload error:', error);
      setIsVideoUploading(false);
      // Reset editing phase jika upload gagal
      setEditingPhase(null);
      
      // Show error notification
      addToast({
        title: "Upload Error",
        description: "Failed to upload reference video. Please try again.",
        color: "danger"
      });
    }
  };

  const handleVideoButtonClick = (phase) => {
   // console.log('handleVideoButtonClick - phase:', phase);
   // console.log('handleVideoButtonClick - phase.name:', phase.name);
    
    // Langsung buka file picker
    if (videoInputRef.current) {
      videoInputRef.current.click();
    } else {
      console.error('videoInputRef is null');
    }
    // Simpan phase name untuk konteks upload
    setEditingPhase(phase.name);
  };

  // Handle animation file upload for phase
  const handleAnimationUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type (allow common animation file types)
    const allowedTypes = ['.txt', '.json', '.fbx', '.obj', '.dae', '.3ds', '.blend'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(fileExtension)) {
      addToast({
        title: "Invalid File",
        description: "Please select a valid animation file (.txt, .json, .fbx, .obj, .dae, .3ds, .blend)",
        color: "warning"
      });
      return;
    }

    // Validate file size (max 20MB)
    if (file.size > 20 * 1024 * 1024) {
      addToast({
        title: "File Too Large",
        description: "File size should be less than 20MB",
        color: "warning"
      });
      return;
    }

    // Upload to server
    setIsAnimationUploading(true);
    try {
      // Validate editingPhase before calling API
      const validSections = ['begin', 'middle', 'end'];
      if (!editingPhase || !validSections.includes(editingPhase)) {
        throw new Error(`Invalid editingPhase: ${editingPhase}. Must be one of: ${validSections.join(', ')}`);
      }
      
      // Use the new uploadExcerciseRefAnim method with section parameter
      const response = await uploadExcerciseRefAnim(localItem.id, editingPhase, file);
      
      
      // The API response already contains the updated phase data
      const updatedPhase = response.data;
      
      // Update local state
      const updatedReferenceMotionData = [...(localItem.referenceMotionData || [])];
      const phaseIndex = updatedReferenceMotionData.findIndex(p => p.name === editingPhase);
      
      if (phaseIndex !== -1) {
        updatedReferenceMotionData[phaseIndex] = updatedPhase;
      } else {
        updatedReferenceMotionData.push(updatedPhase);
      }
      
      // Update itemData state
      setItemData(prev => prev.map(item => ({
        ...item,
        beginData: updatedPhase.name === 'begin' ? updatedPhase : item.beginData,
        middleData: updatedPhase.name === 'middle' ? updatedPhase : item.middleData,
        endData: updatedPhase.name === 'end' ? updatedPhase : item.endData
      })));


      
      // Update the local item state to reflect the new animation
      const updatedLocalItem = {
        ...localItem,
        referenceMotionData: updatedReferenceMotionData
      };
      
      // Update local state
      setLocalItem(updatedLocalItem);
      
      // Call the parent callback to update the exercise list
      if (onItemUpdated) {
        onItemUpdated(updatedLocalItem);
      }
      
      // Reset editing phase setelah upload berhasil
      setEditingPhase(null);
      
      addToast({
        title: "Success",
        description: `${editingPhase} phase reference animation uploaded successfully`,
        color: "success"
      });
      
      setIsAnimationUploading(false);
    } catch (error) {
      console.error('Upload error:', error);
      setIsAnimationUploading(false);
      // Reset editing phase jika upload gagal
      setEditingPhase(null);
      
      // Show error notification
      addToast({
        title: "Upload Error",
        description: "Failed to upload reference animation file. Please try again.",
        color: "danger"
      });
    }
  };

  const handleAnimationButtonClick = (phase) => {
    // Langsung buka file picker tanpa set editing phase
    animationInputRef.current?.click();
    // Simpan phase name untuk konteks upload
    setEditingPhase(phase.name);
  };

  const renderBasicInfo = () => (
    <div className="space-y-6">
      {/* Exercise Header with Thumbnail, Video, and Upload functionality */}
      <div className="mb-6">
        <ExerciseHeader item={localItem} onChange={(updatedItem) => {
                 // Update the item in parent component
          // Update local state
          setLocalItem(updatedItem);
          // Call parent callback to update the item
          if (onItemUpdated) {
            onItemUpdated(updatedItem);
          }
        }}/>
      </div>
    </div>
  );

  const renderCloudViz = () => {
    const sortedPhases = [...(localItem.referenceMotionData || [])].sort((a, b) => {
      const order = { begin: 0, middle: 1, end: 2 };
      return order[a.name] - order[b.name];
    });
    const selectedPreviewPhase = sortedPhases.find((phase) => phase.name === selectedPreviewPhaseName) || null;

    return (
      <div>
        <div className="mb-3 flex flex-wrap gap-2">
          {sortedPhases.map((phase) => (
            <Button
              key={`preview-${phase.name}`}
              size="sm"
              color={selectedPreviewPhaseName === phase.name ? 'primary' : 'default'}
              variant={selectedPreviewPhaseName === phase.name ? 'solid' : 'bordered'}
              onPress={() => setSelectedPreviewPhaseName(phase.name)}
            >
              {phase.name}
            </Button>
          ))}
        </div>

        <CloudVizPlayer
          tsframePath={selectedPreviewPhase?.path || ''}
          userId={selectedPreviewPhase ? `exercise-${localItem.id}-${selectedPreviewPhase.name}` : `exercise-${localItem.id}`}
        />
      </div>
    );
  };

  const renderReferenceMotion = () => (
    <div>
      {/* Reference Motion Phase Information */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Motion Phase Details
        </h3>
        <div className="grid grid-cols-3 gap-4">
          {[...(localItem.referenceMotionData || [])].sort((a, b) => {
            const order = { begin: 0, middle: 1, end: 2 };
            return order[a.name] - order[b.name];
          }).map((phase, index) => (
            <Card key={index} className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border border-blue-200/50 dark:border-blue-700/30 shadow-md">
            <CardBody>
                             <div className="flex items-center justify-between mb-3">
                 <div className="flex items-center gap-2">
                   <h4 className="text-md font-semibold text-blue-900 dark:text-blue-100 capitalize">
                     {phase.name} Phase
                   </h4>
                 </div>
                 <div className="flex items-center gap-2">
                   {/* Show pending changes indicator */}
                   {pendingChanges[phase.name] && (
                     <div className="flex items-center gap-1">
                       <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                       <span className="text-xs text-yellow-600 dark:text-yellow-400">Modified</span>
                     </div>
                   )}
                   {/* Delete button - only for optional phases */}
                  {phase.name !== 'begin' && (
                     <button
                       className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                       onClick={() => handleRemovePhase(phase.name)}
                       title="Delete this phase"
                     >
                       <TrashIcon className="h-4 w-4" />
                     </button>
                   )}
                 </div>
               </div>
              
                  <div className="space-y-3">
                    {/* Video Player */}
                    <div className="mb-4">
                      <div className="relative bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden group">
                        {phase.videoPath ? (
                          <video
                            className="w-full h-32 object-cover"
                            controls
                            preload="metadata"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <source src={phase.videoPath} type="video/mp4" />
                            <source src={phase.videoPath} type="video/webm" />
                            <source src={phase.videoPath} type="video/ogg" />
                            Your browser does not support the video tag.
                          </video>
                        ) : (
                          <div className="w-full h-32 flex items-center justify-center bg-gray-200 dark:bg-gray-700">
                            <div className="text-center">
                              <VideoCameraIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                              <p className="text-sm text-gray-500 dark:text-gray-400">No video uploaded</p>
                            </div>
                          </div>
                        )}
                        
                        {/* Change Video Button - Hover */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center pointer-events-none">
                          <Button
                            size="sm"
                            color="primary"
                            variant="solid"
                            startContent={<VideoCameraIcon className="h-4 w-4" />}
                            className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-auto z-10"
                            onPress={() => {
                              handleVideoButtonClick(phase);
                            }}
                            disabled={isVideoUploading}
                          >
                            {isVideoUploading ? 'Uploading...' : 'Change Video'}
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Duration Display */}
                    <div>
                      <label className="text-sm font-medium text-blue-700 dark:text-blue-300">Duration</label>
                      <p className="text-sm text-blue-600 dark:text-blue-200">{phase.duration}s</p>
                    </div>
                    
                    {/* Animation Path Display */}
    <div>
                      <label className="text-sm font-medium text-blue-700 dark:text-blue-300">Animation Path</label>
                      <p className="text-sm text-blue-600 dark:text-blue-200 truncate">{phase.path || 'Not set'}</p>
                    </div>
                    
                    {/* Upload Animation File Button */}
                    <div className="pt-2">
                      <Button 
                        size="sm" 
                        color="primary" 
                        variant="bordered" 
                        startContent={<FolderIcon className="h-4 w-4" />}
                        onPress={() => handleAnimationButtonClick(phase)}
                        disabled={isAnimationUploading}
                        className="w-full"
                      >
                        {isAnimationUploading ? 'Uploading...' : 'Upload Animation File'}
                      </Button>
                    </div>
                  </div>
                         </CardBody>
           </Card>
         ))}
         </div>
       </div>
             <KeteranganDivider text="Bones Reference Parameters"/>
       
       {/* Add Bone Button */}
       <div className="mb-4 flex justify-end gap-2">
         <Button
           size="sm"
           color="secondary"
           variant="bordered"
           onPress={handleOpenMirrorModal}
         >
           Mirror To...
         </Button>
         <Button
           size="sm"
           color="primary"
           variant="bordered"
           startContent={<span className="text-lg font-bold">+</span>}
           onPress={() => handleAddBone()}
         >
           Add Bone
         </Button>
       </div>
       
       {/* Save/Discard Changes Header */}
      {hasUnsavedChanges && (
        <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ClockIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                You have unsaved changes in reference motion data
              </span>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                color="danger"
                variant="bordered"
                startContent={<XMarkIcon className="h-4 w-4" />}
                onPress={handleDiscardChanges}
                disabled={isSaving}
              >
                Discard
              </Button>
              <Button
                size="sm"
                color="success"
                variant="solid"
                startContent={<CheckIcon className="h-4 w-4" />}
                onPress={handleSaveAllChanges}
                disabled={isSaving}
                isLoading={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <div className="bg-gray-100 dark:bg-gray-300 p-4 rounded-lg border border-gray-200 dark:border-gray-300 shadow-sm">
        <Accordion 
          dividerProps={{className: "bg-blue-500 my-1"}}
          defaultExpandedKeys={defaultExpandedKeys} 
          selectionMode="multiple"
          className="gap-0"
          itemClasses={{
            base: "mb-0",
            title: "py-0 px-4",
            content: "px-1 py-0 mt-0"
          }}
        >
        {itemData.map((data)=>(
             <AccordionItem 
               key={data.bone} 
               aria-label={data.bone} 
               title={
                 <div className="flex items-center justify-between w-full">
                   <span>{data.bone}</span>
                   <div
                     className="ml-2 p-1 text-red-500 hover:text-red-700 cursor-pointer"
                     onMouseDown={(e) => {
                       e.preventDefault();
                       e.stopPropagation();
                     }}
                     onClick={() => handleRemoveBone(data.bone)}
                   >
                     <TrashIcon className="h-4 w-4" />
                   </div>
                 </div>
               }
             >
            <div className="flex flex-row gap-4">
                <div className="basis-1/3">
                  <ReferenceMotion part={data.bone} data={data.beginData} onChange={handleBoneChange}></ReferenceMotion>
                </div>
                <div className="basis-1/3">
                     <ReferenceMotion part={data.bone} data={data.middleData} onChange={handleBoneChange}></ReferenceMotion>
                </div>
                <div className="basis-1/3">
                     <ReferenceMotion part={data.bone} data={data.endData} onChange={handleBoneChange}></ReferenceMotion>
                </div>
            </div>
          </AccordionItem>
           ))}
        </Accordion>
      </div>
    </div>
  );

  const renderMotionRecording = () => (
    <div className="space-y-6">
      {/* Motion Recording Data Card */}
      <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <DocumentTextIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              Motion Recording Data
            </h3>
          </div>
          <Button
            size="sm"
            color="primary"
            variant="bordered"
            startContent={<DocumentTextIcon className="h-4 w-4" />}
            onPress={handleMotionRecordingButtonClick}
            disabled={isMotionRecordingUploading}
          >
            {isMotionRecordingUploading ? 'Uploading...' : 'Upload Recording'}
          </Button>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
            <label className="text-sm font-medium text-purple-700 dark:text-purple-300">File Path</label>
            <p className="text-sm text-purple-600 dark:text-purple-200">
              {localItem.motionRecordingData?.path || 'No file uploaded'}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
            <label className="text-sm font-medium text-purple-700 dark:text-purple-300">Format</label>
            <p className="text-sm text-purple-600 dark:text-purple-200">
              {localItem.motionRecordingData?.format || 'Not specified'}
            </p>
          </div>
          </div>
        
        {!localItem.motionRecordingData?.path && (
          <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              No motion recording data uploaded. Please upload a .txt file containing the motion recording data.
            </p>
            </div>
          )}
      </div>

      <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Reference Motion Data Structure
        </h3>
        <div className="space-y-2">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            This exercise contains {localItem.referenceMotionData?.length || 0} motion phases:
          </p>
          <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
            {localItem.referenceMotionData?.map((phase, index) => (
              <li key={index}>
                <strong>{phase.name}</strong> (Order: {phase.order}) - Duration: {phase.duration}s
              </li>
            ))}
          </ul>
        </div>
      </div>

    </div>
    );
 
  return (
    <div>
      <div className="mt-6">
        <Tabs 
          selectedKey={selectedTab} 
          onSelectionChange={setSelectedTab}
          className="w-full"
          color="primary"
          variant="underlined"
        >
          <Tab 
            key="basic" 
            title={
              <div className="flex items-center gap-2">
                <span>Basic Info</span>
              </div>
            }
          >
            {renderBasicInfo()}
          </Tab>
          
          <Tab 
            key="reference" 
            title={
              <div className="flex items-center gap-2">
                <span>Reference Motion</span>
                {hasUnsavedChanges && (
                  <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                )}
              </div>
            }
          >
            {renderReferenceMotion()}
          </Tab>
          
          <Tab 
            key="cloudviz" 
            title={
              <div className="flex items-center gap-2">
                <span>Avatar Visualization</span>
              </div>
            }
          >
            {renderCloudViz()}
          </Tab>

          <Tab 
            key="recording" 
            title={
              <div className="flex items-center gap-2">
                <span>Motion Recording</span>
              </div>
            }
          >
            {renderMotionRecording()}
          </Tab>
        </Tabs>
      </div>
      
      {/* Hidden File Inputs - Global access */}
            <input
        ref={motionRecordingInputRef}
        type="file"
        accept=".txt"
        onChange={handleMotionRecordingUpload}
        className="hidden"
        disabled={isMotionRecordingUploading}
      />
      
             <input
         ref={videoInputRef}
         type="file"
         accept="video/*"
         onChange={handleVideoUpload}
         className="hidden"
         disabled={isVideoUploading}
       />
       
            <input
         ref={animationInputRef}
              type="file"
         accept=".txt,.json,.fbx,.obj,.dae,.3ds,.blend"
         onChange={handleAnimationUpload}
         className="hidden"
         disabled={isAnimationUploading}
               />
       
       {/* Add Bone Modal */}
       <Modal isOpen={isAddBoneModalOpen} onClose={() => setIsAddBoneModalOpen(false)} size="md">
         <ModalContent>
           <ModalHeader>Add Bone</ModalHeader>
           <ModalBody>
             <div className="space-y-4">
               <p className="text-sm text-gray-600 dark:text-gray-400">
                 Select a bone to add to all phases:
               </p>
               <div className="grid grid-cols-2 gap-2">
                 {bodyParts.map((bone) => {
                   // Check if bone is already active
                   const isActive = itemData.some(item => item.bone === bone);
                   
                   return (
                     <Button
                       key={bone}
                       size="sm"
                       color={isActive ? "default" : "primary"}
                       variant={isActive ? "bordered" : "solid"}
                       onPress={() => !isActive && handleAddSelectedBone(bone)}
                       disabled={isActive}
                       className="justify-start"
                     >
                       {bone}
                       {isActive && <span className="ml-2 text-xs">(Added)</span>}
                     </Button>
                   );
                 })}
               </div>
          </div>
           </ModalBody>
           <ModalFooter>
             <Button color="danger" variant="light" onPress={() => setIsAddBoneModalOpen(false)}>
              Cancel
             </Button>
           </ModalFooter>
         </ModalContent>
       </Modal>

       <Modal isOpen={isMirrorModalOpen} onClose={handleCloseMirrorModal} size="md">
         <ModalContent>
           <ModalHeader>Mirror Reference Motion</ModalHeader>
           <ModalBody>
             <div className="space-y-4">
               <p className="text-sm text-gray-600 dark:text-gray-400">
                 Select the target exercise to overwrite with mirrored reference motion.
               </p>
               <Select
                 label="Target Exercise"
                 selectedKeys={mirrorTargetId ? [String(mirrorTargetId)] : []}
                 onSelectionChange={(keys) => {
                   const [value] = Array.from(keys);
                   setMirrorTargetId(value || '');
                 }}
                 isDisabled={isMirrorLoading}
                 placeholder={isMirrorLoading ? "Loading exercises..." : "Choose exercise"}
               >
                 {mirrorOptions.map((exercise) => (
                   <SelectItem
                     key={String(exercise.id)}
                     isDisabled={String(exercise.id) === String(localItem.id)}
                   >
                     {`${exercise.id} - ${exercise.title || 'Untitled'}`}
                   </SelectItem>
                 ))}
               </Select>
               <p className="text-xs text-red-500">
                 This will overwrite the target exercise's reference motion (movementRules & maxAngleLimits).
               </p>
             </div>
           </ModalBody>
           <ModalFooter>
             <Button color="danger" variant="light" onPress={handleCloseMirrorModal}>
               Cancel
             </Button>
             <Button
               color="primary"
               variant="solid"
               onPress={handleConfirmMirror}
               isLoading={isMirrorSubmitting}
               disabled={isMirrorSubmitting}
             >
               {isMirrorSubmitting ? 'Mirroring...' : 'Mirror'}
             </Button>
           </ModalFooter>
         </ModalContent>
       </Modal>
          </div>
   );
 };

export default ExcerciseDetail;
